import * as Dialog from '@radix-ui/react-dialog';
import { Close } from '@sds-system/assets';
import { theme } from '@sds-system/theme';
import { deepMerge, useGetInternalProps } from '@sds-system/utils';
import {
  StyledDialogOverlay,
  StyledModalCloseButton,
  StyledModalContentWrapper,
  StyledModalRoot,
  StyledModalTitleWrapper,
} from './Modal.styled';
import type { ModalProps } from './Modal.types';

export const Modal = (props: ModalProps) => {
  const internalProps = useGetInternalProps('Modal');
  const { styledComponents, ...modalProps } = props;
  const {
    title,
    isOpen,
    hasCloseButton = true,
    exitHandler,
    defaultOpen,
    children,
    container,
    width = 'max-content',
    height,
    isResponsive,
    isDuplication,
  } = modalProps;

  const mergedStyledComponentsProps = deepMerge(
    internalProps?.styledComponents || {},
    styledComponents || {},
  );

  const innerIsResponsive = (() => {
    // 직접 전달된 isResponsive가 있으면 최우선 적용
    if (typeof isResponsive !== 'undefined') {
      return isResponsive;
    }

    // 테마에서 설정된 값이 있으면 그 다음 우선순위
    if (typeof internalProps?.isResponsive !== 'undefined') {
      return internalProps.isResponsive;
    }

    // 아무 설정이 없으면 기본값 true
    return true;
  })();

  return (
    <Dialog.Root defaultOpen={defaultOpen} onOpenChange={exitHandler} open={isOpen}>
      <Dialog.Portal container={container}>
        <StyledDialogOverlay
          className={isDuplication ? 'radix-dialog-overlay-duplicate' : 'radix-dialog-overlay'}
          zIndex={isDuplication ? theme.zIndex[10000] : theme.zIndex[9999]}
        />
        <StyledModalRoot
          height={height}
          isResponsive={innerIsResponsive}
          width={width}
          zIndex={isDuplication ? theme.zIndex[10000] : theme.zIndex[9999]}
          {...(mergedStyledComponentsProps?.root && mergedStyledComponentsProps?.root)}
        >
          {hasCloseButton && (
            <StyledModalCloseButton onClick={exitHandler} size='XS' variant='ghost.assistive'>
              <Close height={24} width={24} />
            </StyledModalCloseButton>
          )}

          {title && (
            <StyledModalTitleWrapper
              {...(mergedStyledComponentsProps?.title && mergedStyledComponentsProps.title)}
            >
              {title}
            </StyledModalTitleWrapper>
          )}

          {children && <StyledModalContentWrapper>{children}</StyledModalContentWrapper>}
        </StyledModalRoot>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
